import {Alert, Button, message, Modal, Progress, Space, Upload} from "antd";
import React, {useState} from "react";
import {UploadProps} from "antd/es/upload";
import {authHeader, download} from "../../Utilities";
import {HttpRequestHeader} from "antd/es/upload/interface";
import {ResultReference} from "../../services/ApiModels/ResultReference";
import AllResultsService from "../../services/AllResultsService";
import ImageViewer from "./ImageViewer";

export function BulkUpdateDrawingModal({ imageViewer, resultReference, setVisible, visible}: {
    imageViewer: ImageViewer
    resultReference: ResultReference
    setVisible: React.Dispatch<React.SetStateAction<boolean>>
    visible: boolean,
}) {
    const showWarning = imageViewer.getCanvasObjects().some(
        x => x.isGraphObject() || x.hasLinks()
    );

    return (
        <Modal
            title={`Bulk Update Annotation Objects`}
            visible={visible}
            onCancel={() => setVisible(false)}
            destroyOnClose={true}
            footer={null}
        >
            <Space direction={"vertical"} style={{ width: "100%" }}>
                {showWarning && (
                    <Alert
                        message="Warning"
                        description="Unable to export the document correctly. There are piping objects or objects with links"
                        type="warning"
                        showIcon
                    />
                )}

                <DownloadExcel resultReference={resultReference} />
                <UploadExcel
                    resultReference={resultReference}
                />
            </Space>
        </Modal>
    );
}

type DownloadExcelProps = {
    resultReference: ResultReference
}

function DownloadExcel({ resultReference }: DownloadExcelProps ) {
    const [progress, setProgress] = useState({status: "", percent: 0});
    const [spinning, setSpinning] = useState(false);

    const start = () => {
        setSpinning(true);
        AllResultsService.getExportForBulkUpdate(resultReference)
            .then((resp) => {
                console.log(resp.data);
                const blob = new Blob([resp.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
                download(blob, `annotation_objects.xlsx`);
                setProgress({ status: "success", percent: 100 });
                message.success("Export successful!");
            })
            .catch((error) => {
                console.error(error);
                setProgress({ status: "exception", percent: 0 });
                message.error("Failed to export");
            })
            .finally(() => {
                setSpinning(false);
            });
    };

    return (
        <>
            <Button type={"primary"} onClick={() => start()}>
                Download excel
            </Button>
            {spinning && (
                <div>
                    {progress.status}
                    <Progress percent={progress.percent}/>
                </div>
            )}
        </>
    );
}

type UploadExcelProps = {
    resultReference: ResultReference
}

function UploadExcel({ resultReference }: UploadExcelProps) {
    const [spinning, setSpinning] = useState(false);
    const [error, setError] = useState<string | undefined>(undefined);

    const uploadProps: Partial<UploadProps> = {
        accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        name: "file",
        headers: authHeader() as HttpRequestHeader,
        customRequest: async ({ file, onSuccess, onError }) => {
            if (!(file instanceof File)) {
                message.error("Invalid file format!");
                return;
            }
            try {
                setSpinning(true);
                const response = await AllResultsService.getBulkUpdateFromFile(resultReference, file);
                onSuccess?.(response.data, file);
                message.success("File uploaded successfully!");
                window.location.reload();
            } catch (error) {
                console.error("Upload error:", error);
                setError("Failed to upload file!");
                message.error("Failed to upload file!");
            } finally {
                setSpinning(false);
            }
        }
    };

    return (
        <>
            <Upload {...uploadProps} showUploadList={true}>
                <Button type={"primary"}>Upload excel</Button>
            </Upload>
            {error != null && (
                <Alert message={error} type={"error"}/>
            )}
        </>
    );
}
